import { Query, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  EditCardViewResponse,
  GlobalSearchCardsResponse,
  ListCardsResponseWithProjectName,
} from "tng-address-collection-shared/types/apiTypes.ts";
import { useAuthentication } from "../auth/useAuthentication.ts";
import { queryKeys } from "./queryKeys.ts";

export const useGetCardEditView = (
  id: string | undefined,
  { projectId }: { projectId: string },
) => {
  const { getToken, isAuthenticated } = useAuthentication();
  const queryClient = useQueryClient();

  return useQuery({
    queryKey: queryKeys.cardEditView(projectId, id),
    queryFn: async ({ signal }): Promise<EditCardViewResponse> => {
      const token = await getToken();
      const response = await fetch(`/api/projects/${projectId}/cards/${id}`, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
        },
        signal,
      });

      if (!response.ok) {
        throw new Error("Server returned status outside the 200 range.");
      }

      return EditCardViewResponse.parse(await response.json());
    },
    enabled: id != null && isAuthenticated,
    // Read-your-writes: don't refetch from the lagging view on drawer reopen
    staleTime: 10_000,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    placeholderData: (_): EditCardViewResponse | undefined => {
      const getCardFromList = () =>
        queryClient
          .getQueryData<ListCardsResponseWithProjectName>(
            queryKeys.cardsInProject(projectId),
          )
          ?.items?.find((x) => x.id === id);

      const getCardFromLatestSearch = () => {
        const searchQueries = queryClient.getQueryCache().findAll({
          queryKey: queryKeys.globalSearchCardsAllResults(),
          exact: false,
        }) as Query<GlobalSearchCardsResponse>[];

        const latestSearch = searchQueries.sort(
          (a, b) => b.state.dataUpdatedAt - a.state.dataUpdatedAt,
        )[0];
        return latestSearch?.state.data?.cards.find((x) => x.id === id);
      };

      const cardFromCache = getCardFromList() ?? getCardFromLatestSearch();
      if (!cardFromCache) return undefined;
      const hasContactDetailsWarning =
        "hasContactDetailsWarning" in cardFromCache &&
        cardFromCache.hasContactDetailsWarning;

      return {
        id: cardFromCache.id,
        projectId: cardFromCache.projectId,
        senderId: cardFromCache.senderId,
        departmentName: cardFromCache.departmentName,
        comment: cardFromCache.comment,
        hasContactDetailsWarning,
        contactDetailsWarning: hasContactDetailsWarning ? {} : undefined,
        formality: cardFromCache.formality,
        manualDispatch: cardFromCache.manualDispatch,
        receivesGift: cardFromCache.receivesGift,
        recipient: {
          preferredLanguage: cardFromCache.language,
          title: cardFromCache.title,
          firstName: cardFromCache.firstName,
          lastName: cardFromCache.lastName,
          email: cardFromCache.email,
          companyName: cardFromCache.companyName,
          salutationKind: cardFromCache.salutation,
        },
      };
    },
  });
};
